import { useState, useEffect } from 'react';
import "./TextFileViewer.css";

type TextFileViewerProps = {
    url: string;
    fileType: string;
}

export const TextFileViewer = ({ url, fileType }: TextFileViewerProps) => {
    const [content, setContent] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchContent = async () => {
            try {
                setLoading(true);
                const response = await fetch(url);
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                
                const text = await response.text();
                setContent(text);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load file');
            } finally {
                setLoading(false);
            }
        };

        fetchContent();
    }, [url]);

    if (loading) {
        return (
            <div className="text-preview-loading">
                <div className="loading-spinner"></div>
                <p>Loading file content...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-preview-error">
                <p>Error loading file: {error}</p>
                <a href={url} target="_blank" rel="noopener noreferrer" className="preview-link">
                    Open file in new tab
                </a>
            </div>
        );
    }

    // Format content based on file type
    const formatContent = () => {
        if (fileType === 'json') {
            try {
                const parsed = JSON.parse(content);
                return <pre className="json-content">{JSON.stringify(parsed, null, 2)}</pre>;
            } catch {
                return <pre className="text-content">{content}</pre>;
            }
        }
        
        if (fileType === 'csv') {
            const lines = content.split('\n');
            const headers = lines[0]?.split(',') || [];
            const data = lines.slice(1).filter(line => line.trim());
            
            return (
                <div className="csv-content">
                    <table className="csv-table">
                        <thead>
                            <tr>
                                {headers.map((header, i) => (
                                    <th key={i}>{header.trim()}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((line, i) => (
                                <tr key={i}>
                                    {line.split(',').map((cell, j) => (
                                        <td key={j}>{cell.trim()}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
        }
        
        if (fileType === 'md' || fileType === 'markdown') {
            // Basic markdown rendering (you could integrate a markdown library here)
            return <pre className="markdown-content">{content}</pre>;
        }
        
        // Default text display
        return <pre className="text-content">{content}</pre>;
    };

    return (
        <div className="text-preview-container">
            <div className="text-preview-header">
                <span className="file-type-indicator">{fileType.toUpperCase()}</span>
                <span className="content-length">{content.length} characters</span>
            </div>
            <div className="text-preview-body">
                {formatContent()}
            </div>
        </div>
    );
};
